<script>
import Type from '@shell/components/nav/Type';
export default {
  name: 'Group',

  components: { Type },

  props: {
    depth: {
      type:    Number,
      default: 0,
    },

    idPrefix: {
      type:     String,
      required: true,
    },

    group: {
      type:     Object,
      required: true,
    },

    childrenKey: {
      type:    String,
      default: 'children',
    },

    canCollapse: {
      type:    Boolean,
      default: true,
    },

    showHeader: {
      type:    Boolean,
      default: true,
    },

    fixedOpen: {
      type:    Boolean,
      default: false,
    }
  },

  data() {
    const id = (this.idPrefix || '') + this.group.name;

    return { id, expanded: false };
  },

  computed: {
    hasChildren() {
      return this.group.children?.length > 0;
    },

    hasOverview() {
      return this.group.children?.[0]?.overview;
    },

    onlyHasOverview() {
      return this.group.children && this.group.children.length === 1 && this.hasOverview;
    },

    isOverview() {
      if (this.group.children && this.group.children.length > 0) {
        const grp = this.group.children[0];
        const overviewRoute = grp?.route;

        if (overviewRoute && grp.overview) {
          const route = this.$router.resolve(overviewRoute || {});

          return this.$route.fullPath === route?.route?.fullPath;
        }
      }

      return false;
    },

    isExpanded: {
      get() {
        return this.fixedOpen || this.group.isRoot || !!this.expanded;
      },
      set(v) {
        this.expanded = v;
      }
    }
  },

  methods: {
    expandGroup() {
      this.isExpanded = true;
      this.$emit('expand', this.group);
    },

    groupSelected() {
      this.isHeaderActive();
      // Don't auto-select first group entry if we're already expanded and contain the currently-selected nav item
      if (this.hasActiveRoute() && this.isExpanded) {
        return;
      }

      this.expandGroup();

      const items = this.group[this.childrenKey];

      // Navigate to one of the child items (by default the first child)
      if (items && items.length > 0) {
        let index = 0;

        // If there is a default type, use it
        if (this.group.defaultType) {
          const found = items.findIndex(i => i.name === this.group.defaultType);

          index = (found === -1) ? 0 : found;
        }

        const route = items[index].route;

        if (route) {
          this.$router.replace(route);
        }
      }
    },

    isHeaderActive() {
      const header = document.getElementsByClassName('header');

      // 循环header的DOM，如果有active-header类，先移除
      for (const ele of header) {
        if (ele.className.includes('active-header')) {
          ele.classList.remove('active-header');
        }
      }
      let liNode = '';

      // 获取拥有nuxt-link-exact-active类的dom，找到header的dom，加入active-header类
      setTimeout(() => {
        liNode = document.getElementsByClassName('nuxt-link-exact-active')[0];
        /*
        dom结构如下
        <header></header>
        <ul>
          <li></li>
        </ul>
         */
        const headerNode = liNode?.parentNode?.previousElementSibling;

        if (headerNode) {
          headerNode.classList.add('active-header');
        }
      });
    },

    selectType() {
      this.groupSelected();
      this.close();
    },

    close() {
      this.$emit('close');
    },

    // User clicked on the expander icon, so toggle the expansion so the user can see inside the group
    peek($event) {
      this.isExpanded = !this.isExpanded;
      $event.stopPropagation();
    },

    hasActiveRoute(items) {
      if (!items) {
        items = this.group;
      }

      for (const item of items.children) {
        if (item.children && this.hasActiveRoute(item)) {
          return true;
        } else if (item.route) {
          const navLevels = ['cluster', 'product', 'resource'];
          const matchesNavLevel = navLevels.filter(param => !this.$route.params[param] || this.$route.params[param] !== item.route.params[param]).length === 0;
          const withoutHash = this.$route.hash ? this.$route.fullPath.slice(0, this.$route.fullPath.indexOf(this.$route.hash)) : this.$route.fullPath;
          const withoutQuery = withoutHash.split('?')[0];

          if (matchesNavLevel || this.$router.resolve(item.route).route.fullPath === withoutQuery) {
            return true;
          }
        }
      }

      return false;
    },

    syncNav() {
      const refs = this.$refs.groups;

      this.isHeaderActive();

      if (refs) {
        // Only expand one group - so after the first has been expanded, no more will
        let canExpand = true;

        refs.forEach((grp) => {
          if (!grp.group.isRoot) {
            if (canExpand) {
              const isActive = this.hasActiveRoute(grp.group);

              if (isActive) {
                grp.isExpanded = true;
                canExpand = false;
                this.$nextTick(() => grp.syncNav());
              }
            }
          }
        });
      }
    }
  }
};
</script>

<template>
  <div class="accordion" :class="{[`depth-${depth}`]: true, 'expanded': isExpanded, 'has-children': hasChildren}">
    <div v-if="showHeader" class="header" :class="{'active': isOverview, 'noHover': !canCollapse}" @click="groupSelected()">
      <slot name="header">
        <n-link v-if="hasOverview" :to="group.children[0].route" :exact="group.children[0].exact">
          <h6 v-html="group.labelDisplay || group.label" />
        </n-link>
        <h6 v-else v-html="group.labelDisplay || group.label" />
      </slot>
      <i v-if="!onlyHasOverview && canCollapse" class="icon toggle icon-size" :class="{'icon-chevron-down': !isExpanded, 'icon-chevron-up': isExpanded}" @click="peek($event, true)" />
    </div>
    <ul v-if="isExpanded" class="list-unstyled body" v-bind="$attrs">
      <template v-for="(child, idx) in group[childrenKey]">
        <li v-if="child.divider" :key="idx">
          <hr />
        </li>
        <!-- <div v-else-if="child[childrenKey] && hideGroup(child[childrenKey])" :key="child.name">
          HIDDEN
        </div> -->
        <li v-else-if="child[childrenKey]" :key="child.name">
          <Group
            ref="groups"
            :key="id+'_'+child.name+'_children'"
            :id-prefix="id+'_'"
            :depth="depth + 1"
            :children-key="childrenKey"
            :can-collapse="canCollapse"
            :group="child"
            :fixed-open="fixedOpen"
            @selected="groupSelected($event)"
            @expand="expandGroup($event)"
            @close="close($event)"
          />
        </li>
        <Type
          v-else-if="!child.overview || group.name === 'starred'"
          :key="id+'_' + child.name + '_type'"
          :is-root="depth == 0 && !showHeader"
          :type="child"
          :depth="depth"
          @selected="selectType($event)"
        />
      </template>
    </ul>
  </div>
</template>

<style lang="scss" scoped>

  .header {
    position: relative;
    cursor: pointer;
    color: var(--body-text);

    > H6 {
      color: var(--body-text);
      user-select: none;
      text-transform: none;
      font-size: 14px;
      line-height: 22px;

      > I {
        position: absolute;
        right: 0;
        top: 0;
        padding: 10px 7px 16px 7px;
        user-select: none;
      }
    }

    > I {
      position: absolute;
      right: 0;
      top: 0;
      padding: 10px 7px 16px 7px;
      user-select: none;
      color: #96999C;
      font-size: 12px;
    }

    > A {
      display: block;
      padding-left: 10px;
      &:hover{
          background-color: #E7F2FF;
          text-decoration: none;
        }
      &:focus{
        outline:none;
      }
      > H6 {
        font-size: 14px;
        text-transform: none;
      }
    }

    &.active {
      background-color: var(--nav-active);
    }
  }

  .body {
    margin-left: 10px;
    padding: 8px 0;
  }

  .accordion {
    .header {
      &:hover:not(.noHover) {
        // background-color: var(--nav-hover);
        background-color: #E7F2FF;
      }

      > I {
        &:hover {
          // background-color: var(--nav-expander-hover);
          background-color: #E7F2FF;
        }
      }
    }
  }

  .accordion {
    &.depth-0 {
      > .header {
        padding: 9px 0;

        &.noHover {
          cursor: default;
        }

        > H6 {
          font-size: 14px;
          text-transform: none;
          padding-left: 10px;
          align-items: center;
          display: flex;
          overflow: hidden;
          text-overflow: ellipsis;

          > ::v-deep IMG {
            padding: 0 7px;
            user-select: none;
          }
        }
      }

      > .body {
        margin-left: 0;
        background: rgba(185,185,185,0.1);
      }
    }

    &.depth-1 {
      > .header {
        > H6 {
          font-size: 13px;
          line-height: 16px;
          padding: 9px 0 7px 5px !important;
        }
        > I {
          padding: 9px 7px 8px 7px !important;
        }
      }
    }

    &:not(.depth-0) {
      > .header {
        padding-left: 10px;
        > H6 {
          // Child groups that aren't linked themselves
          display: inline-block;
          padding: 5px 0 5px 5px;
        }

        > I {
          position: absolute;
          right: 0;
          top: 0;
          padding: 6px 8px 6px 8px;
        }
      }
    }
  }

 .body ::v-deep > .child.nuxt-link-active,
 .header ::v-deep > .child.nuxt-link-exact-active {
    padding: 0;

    .header {
      color: var(--primary);
    }

    A, A I {
      color: var(--body-text);
    }

    A {
      // background-color: var(--nav-active);
      background-color: #E7F2FF;
      border-left: solid 6px var(--primary);
      color: var(--primary);
    }
  }

  .body ::v-deep > .child {
    margin-bottom: 8px;
    &:last-child {
      margin-bottom: 0;
    }
    A {
      border-left: solid 5px transparent;
      line-height: 22px;
      font-size: 14px;
      padding: 9px 9px 9px 54px;
      &:hover {
        // background-color: var(--nav-expander-hover);
        background-color: #E7F2FF;
      }

      SPAN {
        font-weight: 400;
      }
    }

    A:focus {
      outline: none;
    }
  }
  .active-header {
    color: var(--primary) !important;
    > H6 {
      color: var(--primary) !important;
      > ::v-deep IMG {
        filter: invert(0.5) sepia(1) saturate(10) hue-rotate(200deg) !important;
      }
    }
    > I {
      color: var(--primary);
    }
  }
</style>
